import { Reveal, SectionLabel } from "./Reveal";

const CHAPTERS = [
  {
    n: "01",
    title: "Silent drift",
    body: "What was promised, what was scheduled and what was built diverge quietly. Each system is internally consistent and collectively wrong. The gap surfaces at the client — never internally.",
  },
  {
    n: "02",
    title: "Answer latency",
    body: "Questions whose answers already exist in your own data take hours or days, because they need the right human in the right timezone. Meetings are the current technology for organizational state reconciliation.",
  },
  {
    n: "03",
    title: "Duplicated work",
    body: "Two teams build the same thing because neither could see the other. AI made building cheap enough that this is now routine rather than embarrassing — and nobody is measuring the cost.",
  },
];

const EVIDENCE = [
  { source: "HubSpot", record: "Wells Fargo moved to Proposal Sent", time: "11 Aug · 14:20" },
  { source: "GitHub", record: "Last commit on the cortex repo", time: "10 Aug · 09:04" },
  { source: "Calendar", record: "Technical demo with client, confirmed", time: "27 Aug · 11:00" },
];

export const Manifesto = () => (
  <section id="problem" data-testid="manifesto-section" className="relative bg-white">
    <div className="mx-auto max-w-[1440px] px-5 py-24 md:px-10 md:py-36">
      <SectionLabel index="01" title="The problem we understand" />

      <Reveal className="mt-10">
        <h2 className="max-w-4xl text-[28px] font-bold leading-tight tracking-tight md:text-[40px]">
          Nothing is broken. Every system is telling the truth.{" "}
          <span className="text-black/40">That's exactly the problem.</span>
        </h2>
      </Reveal>

      <Reveal delay={0.1} className="mt-6 max-w-2xl">
        <p className="text-base leading-relaxed text-black/70 md:text-lg">
          We've felt this. The deal says one thing, the repo says another, the calendar doesn't know
          either of them exists. No single tool you run today would catch it — because no single tool
          can see all three facts at once.
        </p>
      </Reveal>

      <div className="mt-16 grid gap-6 lg:grid-cols-12">
        <Reveal className="lg:col-span-7" delay={0.05}>
          <div className="hard-shadow h-full border-2 border-black bg-white" data-testid="case-file-card">
            <div className="flex items-center justify-between border-b-2 border-black px-6 py-4 md:px-8">
              <span className="text-[11px] font-bold uppercase tracking-[0.2em]">Case file — Wells Fargo</span>
              <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-black/40">Detected automatically</span>
            </div>
            <div className="divide-y divide-black/15">
              {EVIDENCE.map((e, i) => (
                <div key={i} className="grid grid-cols-[92px_1fr] items-baseline gap-4 px-6 py-5 md:grid-cols-[120px_1fr_150px] md:px-8">
                  <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-black/50">{e.source}</span>
                  <span className="text-sm font-semibold md:text-base">{e.record}</span>
                  <span className="col-start-2 text-[11px] font-bold uppercase tracking-[0.18em] text-black/50 md:col-start-3 md:text-right">{e.time}</span>
                </div>
              ))}
            </div>
            <div className="border-t-2 border-black bg-black px-6 py-5 text-white md:px-8">
              <p className="text-sm font-semibold leading-relaxed md:text-base">
                Sold on the 11th. Demo booked for the 27th. Nothing built for eleven days. Read
                separately, nothing triggers an alert anywhere. Read together, the gap is unmissable.
              </p>
            </div>
          </div>
        </Reveal>

        <div className="grid gap-6 lg:col-span-5">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.n} delay={0.1 + i * 0.08}>
              <div
                data-testid={`manifesto-chapter-${c.n}`}
                className="group h-full border-2 border-black bg-white p-6 transition-colors duration-150 hover:bg-black hover:text-white md:p-8"
              >
                <div className="flex items-baseline justify-between">
                  <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-black/40 group-hover:text-white/50">
                    Chapter {c.n}
                  </span>
                  <span className="text-3xl font-extrabold tracking-tighter text-black/15 transition-colors duration-150 group-hover:text-white/25">
                    {c.n}
                  </span>
                </div>
                <h3 className="mt-3 text-xl font-bold tracking-tight md:text-2xl">{c.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-black/65 group-hover:text-white/70 md:text-base">
                  {c.body}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);
